import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Checkbox,
  Collapse,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { resources } from '../../core/services/resources';
import { useActiveProject } from '../../core/contexts/ProjectContext';
import { GlassPanel } from '../../shared/components/GlassPanel';
import { MarkdownContent } from '../../shared/components/MarkdownContent';
import { Pill } from '../../shared/components/Pill';
import { hashSemanticColor } from '../../shared/components/semanticColor';
import { VisualizationViewerDialog } from '../../shared/components/VisualizationViewerDialog';
import { useVisualizationImage } from '../documents/useVisualizationImage';
import { SearchEntityDialog } from './SearchEntityDialog';
import type {
  SearchEntityResult,
  SearchResponse,
  SearchSectionResult,
  SearchVisualizationResult,
  TimingNode,
} from '../../core/types/data-models';

const DOCUMENT_RESULTS_LIMIT = 15;

interface DocumentResultGroup {
  documentId: number;
  documentName: string;
  documentType: string | null;
  documentDescription: string | null;
  documentAiSummary: string | null;
  sections: SearchSectionResult[];
}

function buildDocumentGroups(data: SearchResponse): DocumentResultGroup[] {
  const groups = new Map<number, DocumentResultGroup>();

  for (const doc of data.documents) {
    groups.set(doc.documentId, {
      documentId: doc.documentId,
      documentName: doc.documentName,
      documentType: doc.documentType,
      documentDescription: doc.documentDescription,
      documentAiSummary: doc.documentAiSummary,
      sections: [],
    });
  }

  for (const section of data.sections) {
    const existing = groups.get(section.documentId);
    if (existing) {
      existing.sections.push(section);
    } else {
      groups.set(section.documentId, {
        documentId: section.documentId,
        documentName: section.documentName,
        documentType: null,
        documentDescription: null,
        documentAiSummary: null,
        sections: [section],
      });
    }
  }

  return Array.from(groups.values());
}

function SectionResultRow({ section, highlightTerms }: { section: SearchSectionResult; highlightTerms: string[] }) {
  const [open, setOpen] = useState(false);
  const sectionText = section.documentSectionContent || section.documentSectionAiSummary;

  return (
    <Box>
      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        onClick={() => sectionText && setOpen((o) => !o)}
        sx={{ cursor: sectionText ? 'pointer' : 'default', width: 'fit-content' }}
      >
        {sectionText && (
          open ? (
            <ExpandMoreIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 15, color: 'text.secondary' }} />
          )
        )}
        <Typography variant="body2" fontWeight={600}>{section.documentSectionTitle}</Typography>
      </Stack>
      {sectionText && (
        <Collapse in={open} unmountOnExit>
          <MarkdownContent sx={{ mt: 0.25, pl: 2.5, fontSize: 'var(--font-size-sm)' }} highlightTerms={highlightTerms}>
            {sectionText}
          </MarkdownContent>
        </Collapse>
      )}
    </Box>
  );
}

function DocumentResultBlock({
  group,
  expanded,
  onToggle,
  onOpen,
  highlightTerms,
}: {
  group: DocumentResultGroup;
  expanded: boolean;
  onToggle: () => void;
  onOpen: () => void;
  highlightTerms: string[];
}) {
  const hasSections = group.sections.length > 0;
  const summary = group.documentAiSummary || group.documentDescription;

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          {group.documentType && (
            <Typography variant="caption" color="text.secondary">{group.documentType}</Typography>
          )}
          <Typography variant="subtitle2">{group.documentName}</Typography>
          {summary && (
            <MarkdownContent sx={{ mt: 0.5, fontSize: 'var(--font-size-sm)' }} highlightTerms={highlightTerms}>
              {summary}
            </MarkdownContent>
          )}
        </Box>
        <Button size="small" variant="outlined" onClick={onOpen} sx={{ flexShrink: 0 }}>
          Open document
        </Button>
      </Stack>

      {hasSections && (
        <Box sx={{ mt: 1 }}>
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            onClick={onToggle}
            sx={{ cursor: 'pointer', width: 'fit-content' }}
          >
            {expanded ? (
              <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            ) : (
              <ChevronRightIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            )}
            <Typography variant="caption" color="text.secondary">
              {group.sections.length} matching section{group.sections.length === 1 ? '' : 's'}
            </Typography>
          </Stack>
          <Collapse in={expanded} unmountOnExit>
            <Stack spacing={0.75} divider={<Divider />} sx={{ mt: 1, pl: 2.5 }}>
              {group.sections.map((section) => (
                <SectionResultRow key={section.documentSectionId} section={section} highlightTerms={highlightTerms} />
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

function VisualizationThumb({ viz, onClick }: { viz: SearchVisualizationResult; onClick: () => void }) {
  const { src } = useVisualizationImage(viz.id, true);
  return (
    <Box
      onClick={onClick}
      sx={{
        width: 96,
        height: 96,
        borderRadius: 1.5,
        overflow: 'hidden',
        flexShrink: 0,
        bgcolor: 'action.hover',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
      }}
    >
      {src ? (
        <Box component="img" src={src} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : (
        <Skeleton variant="rectangular" width={96} height={96} />
      )}
    </Box>
  );
}

function EntityChip({ entity, onClick }: { entity: SearchEntityResult; onClick: () => void }) {
  return (
    <Box component="button" type="button" onClick={onClick} sx={{ border: 'none', background: 'none', p: 0, cursor: 'pointer' }}>
      <Pill color={hashSemanticColor(entity.entityType)}>{entity.entityName}</Pill>
    </Box>
  );
}

function SearchTypeMenu({
  useFullText,
  useRag,
  onChange,
}: {
  useFullText: boolean;
  useRag: boolean;
  onChange: (next: { useFullText: boolean; useRag: boolean }) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  // At least one search type must stay enabled, so unchecking the only
  // active one is a no-op rather than leaving both off.
  const toggleFullText = () => {
    if (useFullText && !useRag) return;
    onChange({ useFullText: !useFullText, useRag });
  };
  const toggleRag = () => {
    if (useRag && !useFullText) return;
    onChange({ useFullText, useRag: !useRag });
  };

  return (
    <>
      <Tooltip title="Search options">
        <IconButton onClick={(e) => setAnchor(e.currentTarget)} aria-label="Search options">
          <TuneIcon />
        </IconButton>
      </Tooltip>
      <Menu open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)}>
        <MenuItem onClick={toggleFullText}>
          <Checkbox checked={useFullText} size="small" edge="start" disableRipple sx={{ mr: 1 }} />
          <ListItemText primary="Use Full Text" />
        </MenuItem>
        <MenuItem onClick={toggleRag}>
          <Checkbox checked={useRag} size="small" edge="start" disableRipple sx={{ mr: 1 }} />
          <ListItemText primary="Use RAG" />
        </MenuItem>
      </Menu>
    </>
  );
}

function TimingBreakdown({ node, depth = 0 }: { node: TimingNode; depth?: number }) {
  return (
    <Box sx={{ pl: depth ? 1.5 : 0 }}>
      <Typography variant="caption" component="div" sx={{ whiteSpace: 'nowrap', color: 'inherit' }}>
        {node.label}: {node.durationMs}ms
      </Typography>
      {node.children?.map((child, i) => (
        <TimingBreakdown key={`${child.label}-${i}`} node={child} depth={depth + 1} />
      ))}
    </Box>
  );
}

function SearchSummaryRow({ data }: { data: SearchResponse }) {
  const seconds = (data.timing.durationMs / 1000).toFixed(2);

  return (
    <Stack direction="row" justifyContent="center" sx={{ mt: 1 }}>
      <Typography variant="caption" color="text.secondary">
        {data.documents.length} document{data.documents.length === 1 ? '' : 's'} · {data.sections.length} section{data.sections.length === 1 ? '' : 's'} · {data.entities.length} entit{data.entities.length === 1 ? 'y' : 'ies'} · {data.visualizations.length} visualization{data.visualizations.length === 1 ? '' : 's'} found ·{' '}
        <Tooltip title={<TimingBreakdown node={data.timing} />} arrow placement="top">
          <Box component="span" sx={{ cursor: 'help', textDecoration: 'underline dotted' }}>
            search took {seconds}s
          </Box>
        </Tooltip>
      </Typography>
    </Stack>
  );
}

export function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get('q') ?? '';
  const [value, setValue] = useState(q);
  const [selectedEntity, setSelectedEntity] = useState<SearchEntityResult | null>(null);
  const [selectedViz, setSelectedViz] = useState<SearchVisualizationResult | null>(null);
  const [useFullText, setUseFullText] = useState(true);
  const [useRag, setUseRag] = useState(true);
  const navigate = useNavigate();
  const { activeProject } = useActiveProject();

  // Keeps the textarea in sync when the query changes from outside this page
  // (e.g. the header search box, or browser back/forward).
  useEffect(() => setValue(q), [q]);

  const submit = () => {
    const trimmed = value.trim();
    if (trimmed) setSearchParams({ q: trimmed });
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['search', activeProject?.id, q, useFullText, useRag],
    queryFn: () => resources.search(activeProject!.id, q, { useFullText, useRag }),
    enabled: !!q && !!activeProject?.id,
  });

  const hasResults = !!data && (data.visualizations.length > 0 || data.entities.length > 0 || data.sections.length > 0 || data.documents.length > 0);

  const highlightTerms = useMemo(() => q.trim().split(/\s+/).filter(Boolean), [q]);

  const documentGroups = useMemo(() => (data ? buildDocumentGroups(data) : []), [data]);
  const visibleDocumentGroups = documentGroups.slice(0, DOCUMENT_RESULTS_LIMIT);

  const [expandedDocIds, setExpandedDocIds] = useState<Set<number>>(new Set());
  useEffect(() => setExpandedDocIds(new Set()), [data]);

  const expandableDocIds = visibleDocumentGroups.filter((g) => g.sections.length > 0).map((g) => g.documentId);
  const allExpanded = expandableDocIds.length > 0 && expandableDocIds.every((id) => expandedDocIds.has(id));

  const toggleExpandAll = () => {
    setExpandedDocIds(allExpanded ? new Set() : new Set(expandableDocIds));
  };

  const toggleDoc = (id: number) => {
    setExpandedDocIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedVizSection = selectedViz
    ? data?.sections.find(
        (s) => s.documentId === selectedViz.documentId && s.documentSectionId === selectedViz.documentSectionId,
      )
    : undefined;
  const selectedVizDocName = selectedViz
    ? documentGroups.find((g) => g.documentId === selectedViz.documentId)?.documentName
    : undefined;

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="flex-end" sx={{ mb: 2 }}>
        <TextField
          fullWidth
          multiline
          minRows={1}
          maxRows={3}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Search documents, sections, entities, visualizations…"
          autoFocus
        />
        <Tooltip title="Search">
          <span>
            <IconButton color="primary" onClick={submit} disabled={!value.trim()} aria-label="Search">
              <SendIcon />
            </IconButton>
          </span>
        </Tooltip>
        <SearchTypeMenu
          useFullText={useFullText}
          useRag={useRag}
          onChange={(next) => {
            setUseFullText(next.useFullText);
            setUseRag(next.useRag);
          }}
        />
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {!q && (
        <Typography variant="body2" color="text.secondary">
          Type a search above and press Enter.
        </Typography>
      )}

      {q && isLoading && (
        <Typography variant="body2" color="text.secondary">Searching…</Typography>
      )}

      {q && !isLoading && data && (
        <Stack spacing={3}>
          {data.visualizations.length > 0 && (
            <GlassPanel sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Visualizations</Typography>
              <Stack direction="row" spacing={1.5} sx={{ overflowX: 'auto' }}>
                {data.visualizations.map((viz) => (
                  <VisualizationThumb key={viz.id} viz={viz} onClick={() => setSelectedViz(viz)} />
                ))}
              </Stack>
            </GlassPanel>
          )}

          {data.entities.length > 0 && (
            <GlassPanel sx={{ p: 2.5 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>Entities</Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                {data.entities.map((entity) => (
                  <EntityChip key={entity.id} entity={entity} onClick={() => setSelectedEntity(entity)} />
                ))}
              </Stack>
            </GlassPanel>
          )}

          {documentGroups.length > 0 && (
            <GlassPanel sx={{ p: 2.5 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                <Typography variant="subtitle2">Artifacts</Typography>
                <Button size="small" onClick={toggleExpandAll} sx={{ textTransform: 'none' }}>
                  {documentGroups.length} document{documentGroups.length === 1 ? '' : 's'} · {data.sections.length} section{data.sections.length === 1 ? '' : 's'} · {data.entities.length} entit{data.entities.length === 1 ? 'y' : 'ies'} found
                </Button>
              </Stack>
              <Stack spacing={2} divider={<Divider />}>
                {visibleDocumentGroups.map((group) => (
                  <DocumentResultBlock
                    key={group.documentId}
                    group={group}
                    expanded={expandedDocIds.has(group.documentId)}
                    onToggle={() => toggleDoc(group.documentId)}
                    onOpen={() => navigate(`/documents/${group.documentId}`)}
                    highlightTerms={highlightTerms}
                  />
                ))}
              </Stack>
              {documentGroups.length > DOCUMENT_RESULTS_LIMIT && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  Showing {DOCUMENT_RESULTS_LIMIT} of {documentGroups.length} matching documents.
                </Typography>
              )}
            </GlassPanel>
          )}

          {!hasResults && (
            <Typography variant="body2" color="text.secondary">
              No results for &ldquo;{q}&rdquo;.
            </Typography>
          )}

          <SearchSummaryRow data={data} />
        </Stack>
      )}

      <SearchEntityDialog entity={selectedEntity} onClose={() => setSelectedEntity(null)} />
      <VisualizationViewerDialog
        viz={selectedViz}
        title={selectedVizDocName ?? 'Visualization'}
        subtitle={selectedVizSection?.documentSectionTitle}
        markdownContent={selectedVizSection?.documentSectionContent || selectedVizSection?.documentSectionAiSummary}
        onClose={() => setSelectedViz(null)}
      />
    </Box>
  );
}
